import {Banner, Button, Dialog, Pill} from '@wandb/components';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import {convertBytes} from '@wandb/weave/util';
import React from 'react';
import {toast} from 'react-toastify';

import {MeterBar} from '../../../pages/Billing/AccountSettings/ManageUsagePage/MeterBar';
import {useNavbarBottom} from '../../../util/hooks/useNavbarBottom';
import {
  DownloadStateEnum,
  useDownloadArtifactStoreArtifactSize,
  useDownloadArtifactStoreCancelArtifactZipBuild,
  useDownloadArtifactStoreCanDownload,
  useDownloadArtifactStoreCompressionStartedAt,
  useDownloadArtifactStoreDownloadArtifactZip,
  useDownloadArtifactStoreDownloadedBytes,
  useDownloadArtifactStoreDownloadState,
  useDownloadArtifactStoreFileCount,
  useDownloadArtifactStoreHasReferenceFiles,
} from './downloadArtifactStore';
import {
  ARTIFACT_DOWNLOAD_FILE_COUNT_LIMIT,
  ARTIFACT_DOWNLOAD_SIZE_LIMIT,
  formatEta,
  OPFS_AVAILABLE,
} from './utils';

type DownloadArtifactDrawerProps = {
  open: boolean;
  onClose: () => void;
};

export const DownloadArtifactDrawer: React.FC<DownloadArtifactDrawerProps> = ({
  open,
  onClose,
}) => {
  const canDownload = useDownloadArtifactStoreCanDownload();
  const downloadState = useDownloadArtifactStoreDownloadState();
  const artifactSize = useDownloadArtifactStoreArtifactSize();

  const cancelArtifactZipBuild =
    useDownloadArtifactStoreCancelArtifactZipBuild();
  const downloadArtifactZip = useDownloadArtifactStoreDownloadArtifactZip();

  const offsetTop = useNavbarBottom();

  const handleClose = () => {
    if (downloadState === DownloadStateEnum.COMPRESSING) {
      cancelArtifactZipBuild();
    }
    onClose();
  };

  const handleBeginDownload = () => {
    if (downloadState === DownloadStateEnum.READY_TO_DOWNLOAD) {
      downloadArtifactZip();
    }
    onClose();
    toast.success(
      `Your ${convertBytes(artifactSize)} zip download has started.`,
      {position: 'bottom-right'}
    );
  };

  return (
    <Dialog.Drawer
      placement="right"
      isOpen={open}
      onOpenChange={isOpen => !isOpen && handleClose()}
      offsetTop={offsetTop}>
      <TailwindContents>
        <div className="flex h-full min-h-0 flex-col">
          <div className="py-16 px-20">
            <Dialog.Heading mb={0}>Download Artifact</Dialog.Heading>
          </div>
          <div className="h-px bg-wb-border-divider" />
          <div className="px-20 py-24 overflow-y-auto flex-1">
            {canDownload ? (
              <DownloadDrawerInner />
            ) : (
              <CannotDownloadDrawerInner />
            )}
          </div>
          <div className="h-px bg-wb-border-divider" />
          <div className="flex flex-row gap-16 px-24 py-20">
            <div className="flex-grow">
              <Button variant="secondary" onPress={handleClose} fullWidth>
                Cancel
              </Button>
            </div>
            <div className="flex-grow">
              {!canDownload || downloadState === DownloadStateEnum.ERROR ? (
                <Button variant="primary" onPress={handleClose} fullWidth>
                  Done
                </Button>
              ) : downloadState === DownloadStateEnum.COMPRESSING ? (
                <Button isPending variant="primary" fullWidth>
                  Begin download
                </Button>
              ) : downloadState === DownloadStateEnum.READY_TO_DOWNLOAD ? (
                <Button
                  variant="primary"
                  onPress={handleBeginDownload}
                  fullWidth>
                  Begin download
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </TailwindContents>
    </Dialog.Drawer>
  );
};

const DownloadError = ({errorMessage}: {errorMessage: string}) => {
  return (
    <Banner isVisible variant="error" isDismissible={false}>
      <Banner.Body>
        <strong>Download unavailable.</strong> {errorMessage}
      </Banner.Body>
      <Banner.Action
        href="https://docs.wandb.ai/models/artifacts/download-and-use-an-artifact"
        target="_blank">
        Learn more
      </Banner.Action>
    </Banner>
  );
};

const CannotDownloadDrawerInner = () => {
  const artifactSize = useDownloadArtifactStoreArtifactSize();
  const fileCount = useDownloadArtifactStoreFileCount();
  const hasReferenceFiles = useDownloadArtifactStoreHasReferenceFiles();
  let errorMessage: string;
  if (!OPFS_AVAILABLE) {
    errorMessage =
      'Your browser does not support in-browser zip downloads. ' +
      'Please use a recent version of Chrome, Edge, or Firefox, or ' +
      'use the SDK download options.';
  } else if (hasReferenceFiles) {
    errorMessage =
      'This artifact is a reference artifact, which cannot be ' +
      'downloaded via the browser. SDK download options are available.';
  } else if (artifactSize > ARTIFACT_DOWNLOAD_SIZE_LIMIT) {
    errorMessage =
      `Artifact .zip file (${convertBytes(artifactSize)}) exceeds ` +
      `the ${convertBytes(ARTIFACT_DOWNLOAD_SIZE_LIMIT)} limit. ` +
      `SDK download options are available.`;
  } else if (fileCount > ARTIFACT_DOWNLOAD_FILE_COUNT_LIMIT) {
    errorMessage =
      `This artifact contains ${fileCount.toLocaleString()} files, ` +
      `which exceeds the ${ARTIFACT_DOWNLOAD_FILE_COUNT_LIMIT.toLocaleString()} limit. ` +
      `SDK download options are available.`;
  } else {
    errorMessage = 'This artifact contains no files to download.';
  }
  return <DownloadError errorMessage={errorMessage} />;
};

const DownloadDrawerInner = () => {
  const downloadState = useDownloadArtifactStoreDownloadState();
  const downloadedBytes = useDownloadArtifactStoreDownloadedBytes();
  const artifactSize = useDownloadArtifactStoreArtifactSize();
  const compressionStartedAt = useDownloadArtifactStoreCompressionStartedAt();
  const fractionDownloaded =
    artifactSize > 0 ? downloadedBytes / artifactSize : 0;

  const etaText = React.useMemo(() => {
    if (
      downloadState !== DownloadStateEnum.COMPRESSING ||
      compressionStartedAt == null ||
      downloadedBytes === 0
    ) {
      return null;
    }
    // oxlint-disable-next-line react/purity
    const elapsedMs = Date.now() - compressionStartedAt;
    const rate = downloadedBytes / elapsedMs;
    const remainingMs = (artifactSize - downloadedBytes) / rate;
    return remainingMs > 0 ? formatEta(remainingMs) : null;
  }, [downloadState, compressionStartedAt, downloadedBytes, artifactSize]);

  const compressionError =
    'Something went wrong while compressing this artifact. ' +
    'Please try again, or use the available SDK download options.';

  return (
    <div className="flex flex-col gap-20">
      <p>
        To download this artifact, we first need to compress its contents into a
        zip archive ({convertBytes(artifactSize)}).
      </p>
      <div className="rounded-lg border border-wb-border py-16 px-16">
        <div className="flex justify-between">
          <div>File Compression</div>
        </div>
        {(downloadState === DownloadStateEnum.COMPRESSING ||
          downloadState === DownloadStateEnum.READY_TO_DOWNLOAD) && (
          <div className="flex flex-col gap-24">
            <div className="flex justify-between">
              <div className="flex items-end">
                <div className="text-[32px] font-semibold leading-none">
                  {convertBytes(downloadedBytes)}
                </div>
                <div className="ml-6 flex font-semibold text-wb-text-tertiary">
                  <div className="mr-6">/</div>
                  {convertBytes(artifactSize)}
                </div>
              </div>
            </div>
            {downloadState === DownloadStateEnum.COMPRESSING ? (
              <div className="flex flex-col gap-24">
                <MeterBar
                  fraction={fractionDownloaded}
                  isUnlimited={true}
                  size="small"
                />
                {etaText && (
                  <div className="text-sm text-wb-text-tertiary">
                    Time remaining: {etaText}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex justify-start">
                <Pill
                  textValue="Complete"
                  color="green"
                  icon="checkmark-circle"
                />
              </div>
            )}
          </div>
        )}
        {downloadState === DownloadStateEnum.ERROR && (
          <div className="mt-24">
            <DownloadError errorMessage={compressionError} />
          </div>
        )}
      </div>
      <p>
        Once the file compression completes, you'll be able to start the
        download manually.
      </p>
      <p>Leaving this drawer will cancel the file compression and download.</p>
    </div>
  );
};
